import React, { memo } from 'react'
import { Box, Grid } from '@material-ui/core'
import SortHandler from '@v4/components/common/SortHandler'
import clsx from 'clsx'
import Image from '@v4/components/common/Image'
import EditableHTML from '@v4/components/common/EditableHTML'
import ActionButton from '@v4/components/common/Buttons/ActionButton'
import PropTypes from 'prop-types'
import PageMetaData from '@v4/utils/data/PageMetaData'
import EditableContainer from '@v4/components/common/EditableContainer'
import { useContent } from './Content.hooks'

const Content = props => {
	const [
		{
			direction,
			classes,
			withImage,
			withBgImage,
			image,
			isMobile,
			isCenterImage,

			title,
			subtitle,
			text,
			buttonText,
			buttonAction,
			buttonLink,
			extraShortcuts,
			textEditable,
			subtitleEditable,
			titleEditable,
			titleConfig,
			subtitleConfig,
			textConfig,
			buttonTextConfig,
			buttonTextEditable,
			titleKey,
			subtitleKey,
			textKey,
			buttonTextKey,
		},
		{
			handleImageClick,
			handleTitleClick,
			handleSubtitleClick,
			handleTextClick,
			handleButtonClick,
			handleSubtitleChange,
			handleTextChange,
			handleTitleChange,
			handleButtonTextChange,
		},
	] = useContent(props)

	return (
		<>
			{withBgImage && <Image className={classes.bgImage} metaData={image} isBackground />}
			<Grid className={classes.root} spacing={isMobile ? 0 : 5} container justify="center">
				<SortHandler key={isMobile ? 'mobile' : 'desktop'} direction={direction}>
					{withImage && (
						<Grid className={classes.imageGrid} lg={6} md={6} sm={9} xs={12} item>
							<Box className={classes.imageWrapper}>
								<Image metaData={image} className={classes.desktopImage} onClick={handleImageClick} />
							</Box>
						</Grid>
					)}
					<Grid className={classes.wrapContainer} lg={6} md={6} sm={9} xs={12} item>
						<div className={clsx(classes.textWrap)}>
							<EditableContainer
								visible={!!title}
								fieldConfig={titleConfig}
								onAdd={handleTitleChange}
								editable={titleEditable}
								wrapper={({ children }) => <Box mb={2}>{children}</Box>}
								white={withBgImage}
							>
								<EditableHTML
									key={titleKey}
									className={classes.title}
									gutterBottom
									html={title}
									onClick={handleTitleClick}
									extraShortcuts={extraShortcuts}
									editable={titleEditable}
									onChange={handleTitleChange}
									white={withBgImage}
								/>
							</EditableContainer>

							<EditableContainer
								visible={!!subtitle}
								fieldConfig={subtitleConfig}
								onAdd={handleSubtitleChange}
								editable={subtitleEditable}
								wrapper={({ children }) => (
									<Box mb={2.5} className={clsx(!!subtitle && classes.subtitle)}>
										{children}
									</Box>
								)}
								white={withBgImage}
							>
								<EditableHTML
									key={subtitleKey}
									className={classes.subtitleText}
									gutterBottom
									html={subtitle}
									onClick={handleSubtitleClick}
									extraShortcuts={extraShortcuts}
									editable={subtitleEditable}
									onChange={handleSubtitleChange}
									white={withBgImage}
								/>
							</EditableContainer>
							{isCenterImage && (
								<Box mb={2.5} className={classes.mobileImage}>
									<Image metaData={image} onClick={handleImageClick} />
								</Box>
							)}
							<EditableContainer
								visible={!!text}
								fieldConfig={textConfig}
								onAdd={handleTextChange}
								editable={textEditable}
								white={withBgImage}
							>
								<Box>
									<EditableHTML
										key={textKey}
										className={classes.text}
										gutterBottom
										html={text}
										onClick={handleTextClick}
										extraShortcuts={extraShortcuts}
										editable={textEditable}
										onChange={handleTextChange}
										white={withBgImage}
									/>
								</Box>
							</EditableContainer>
							<EditableContainer
								visible={!!buttonText}
								fieldConfig={buttonTextConfig}
								onAdd={handleButtonTextChange}
								editable={buttonTextEditable}
								label="Button"
								wrapper={({ children }) => (
									<Box mt={3.5} className={classes.buttonContainer}>
										{children}
									</Box>
								)}
							>
								<ActionButton
									key={buttonTextKey}
									editable={buttonTextEditable}
									action={buttonAction}
									href={buttonLink}
									className={classes.action}
									onClick={handleButtonClick}
									onChange={handleButtonTextChange}
								>
									{buttonText}
								</ActionButton>
							</EditableContainer>
						</div>
					</Grid>
				</SortHandler>
			</Grid>
		</>
	)
}

Content.propTypes = {
	buttonAction: PropTypes.string,
	buttonLink: PropTypes.string,
	buttonText: PropTypes.string,
	classes: PropTypes.shape({
		root: PropTypes.string,
		imageGrid: PropTypes.string,
		desktopImage: PropTypes.string,
		wrapContainer: PropTypes.string,
		textWrap: PropTypes.string,
		title: PropTypes.string,
		subtitle: PropTypes.string,
		subtitleText: PropTypes.string,
		mobileImage: PropTypes.string,
		text: PropTypes.string,
		buttonContainer: PropTypes.string,
		actions: PropTypes.string,
	}),
	direction: PropTypes.string,
	image: PropTypes.instanceOf(PageMetaData),
	onImageClick: PropTypes.func,
	onTitleClick: PropTypes.func,
	onSubtitleClick: PropTypes.func,
	onTextClick: PropTypes.func,
	onButtonClick: PropTypes.func,
	subtitle: PropTypes.string,
	text: PropTypes.string,
	title: PropTypes.string,
	withImage: PropTypes.bool,
	extraShortcuts: PropTypes.objectOf(PropTypes.string),
	titleEditable: PropTypes.bool,
	subtitleEditable: PropTypes.bool,
	textEditable: PropTypes.bool,
	onTextChange: PropTypes.func,
	onTitleChange: PropTypes.func,
	onSubtitleChange: PropTypes.func,
	buttonTextConfig: PropTypes.object,
	titleConfig: PropTypes.object,
	subtitleConfig: PropTypes.object,
	textConfig: PropTypes.object,
	buttonTextEditable: PropTypes.bool,
	onButtonTextChange: PropTypes.func,
	titleKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	subtitleKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	textKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	buttonTextKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	withMobileImage: PropTypes.instanceOf(PageMetaData),
	isMobile: PropTypes.bool,
	marginMetaTop: PropTypes.instanceOf(PageMetaData),
	marginMetaBottom: PropTypes.instanceOf(PageMetaData),
	mobileAlignment: PropTypes.instanceOf(PageMetaData),
	opacity: PropTypes.number,
	isCenterImage: PropTypes.bool,
	withBgImage: PropTypes.bool,
	mobileButtonWidth: PropTypes.instanceOf(PageMetaData),
	mobileImagePosition: PropTypes.instanceOf(PageMetaData),
}

Content.defaultProps = {
	classes: {},
	buttonLink: null,
}
export default memo(Content)
